import React, { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Plus, Check, Trash2 } from "lucide-react";
import { TriggerRule, TriggerCondition } from "@/types/theme";
import type { Question } from "@/types/theme";
import { useBuilder } from "@/contexts/BuilderContext";

interface TriggerConditionsSectionProps {
  rule: TriggerRule;
  questions: Question[];
  onAddCondition: () => void;
  localConditionValues: Record<string, string>;
  onUpdateCondition: (index: number, updatedCondition: Partial<any>) => void;
  onDeleteCondition: (index: number) => void;
}

export const TriggerConditionsSection: React.FC<TriggerConditionsSectionProps> = ({
  rule,
  questions,
  onAddCondition,
  localConditionValues,
  onUpdateCondition,
  onDeleteCondition,
}) => {
  // Get Data Collection fields from unified context
  const { getSettings } = useBuilder();
  const settings = getSettings();
  const aiConfig = settings?.ai_chat_config;

  // Combine questions and data collection fields for trigger options
  const triggerOptions = useMemo(() => {
    const options: Array<{ id: string; label: string; type: 'question' | 'data_field' }> = [];

    // Add regular questions
    questions.forEach(question => {
      options.push({
        id: question.id,
        label: `Question: ${question.text || question.shortname || 'Untitled'}`,
        type: 'question'
      });
    });

    // Add Data Collection fields if AI-only mode is enabled
    if (aiConfig?.collectUserInfo) {
      // Add required fields
      aiConfig.requiredFields?.forEach(field => {
        const fieldName = typeof field === 'string' ? field : field.name;
        const fieldDesc = typeof field === 'string' ? '' : field.description;
        options.push({
          id: `data_field:${fieldName}`,
          label: `Required Field: ${fieldName}${fieldDesc ? ` (${fieldDesc})` : ''}`,
          type: 'data_field'
        });
      });

      // Add optional fields
      aiConfig.optionalFields?.forEach(field => {
        const fieldName = typeof field === 'string' ? field : field.name;
        const fieldDesc = typeof field === 'string' ? '' : field.description;
        options.push({
          id: `data_field:${fieldName}`,
          label: `Optional Field: ${fieldName}${fieldDesc ? ` (${fieldDesc})` : ''}`,
          type: 'data_field'
        });
      });
    }

    return options;
  }, [questions, aiConfig]);

  // Internal condition value handling
  const handleConditionValueChange = (index: number, value: string) => {
    onUpdateCondition(index, { value });
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h4 className="text-sm font-medium text-gray-700">Conditions</h4>
        <Button
          variant="outline"
          size="sm"
          onClick={onAddCondition}
          className="text-xs"
        >
          <Plus className="h-3 w-3 mr-1" />
          Add Condition
        </Button>
      </div>

      <p className="text-xs text-gray-500">
        Define conditions that must be met for this rule to trigger
      </p>

      <div className="space-y-2">
        {rule.conditions && rule.conditions.length > 0 ? (
          rule.conditions.map((condition, index) => (
            <div key={index} className="flex items-center gap-2">
              <div className="flex items-center">
                <label className="inline-flex items-center me-5 cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={!!condition.questionId}
                    onChange={(e) => {
                      onUpdateCondition(index, {
                        questionId: e.target.checked && questions.length > 0 ? questions[0].id || '' : ''
                      });
                    }}
                  />
                  <span className="checked:bg-blue-500 peer-checked:bg-blue-500 peer-checked:ring-blue-500 peer-checked:ring-2 checked:ring-blue-500 checked:ring-2 bg-gray-200 rounded-full w-4 h-4 flex items-center justify-center">
                    <Check className="hidden peer-checked:block text-white" />
                  </span>
                </label>
              </div>

              <select
                value={condition.questionId || ''}
                onChange={(e) => {
                  onUpdateCondition(index, {
                    questionId: e.target.value
                  });
                }}
                className="flex-1 p-2 border rounded"
              >
                <option value="">Select field or question...</option>
                {triggerOptions.map(option => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>

              <select
                value={condition.operator}
                onChange={(e) => {
                  onUpdateCondition(index, {
                    operator: e.target.value as any
                  });
                }}
                className="w-40 p-2 border rounded"
              >
                <option value="equals">Equals</option>
                <option value="contains">Contains</option>
                <option value="greaterThan">Greater Than</option>
                <option value="lessThan">Less Than</option>
                <option value="between">Between</option>
              </select>

              {/* Value input or select for options */}
              {(() => {
                const selectedQuestion = questions.find(q => q.id === condition.questionId);
                if (
                  selectedQuestion &&
                  (selectedQuestion.type === 'button' || selectedQuestion.type === 'select') &&
                  selectedQuestion.options &&
                  selectedQuestion.options.length > 0
                ) {
                  return (
                    <select
                      value={localConditionValues[`${rule.id}-${index}`] || ''}
                      onChange={e => handleConditionValueChange(index, e.target.value)}
                      className="w-40 p-2 border rounded"
                    >
                      <option value="">Select option</option>
                      {selectedQuestion.options.map((opt, i) => (
                        <option key={i} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  );
                } else {
                  return (
                    <TextInputCondition
                      initialValue={localConditionValues[`${rule.id}-${index}`] || ''}
                      placeholder={condition.operator === 'between' ? "min,max" : "Value"}
                      onSave={(value) => handleConditionValueChange(index, value)}
                    />
                  );
                }
              })()}

              <Button
                variant="ghost"
                size="icon"
                onClick={() => onDeleteCondition(index)}
              >
                <Trash2 className="h-4 w-4 text-red-500" />
              </Button>
            </div>
          ))
        ) : (
          <div className="text-sm text-gray-500 italic">
            No conditions added yet. Add a condition to define when this rule should trigger.
          </div>
        )}
      </div>
    </div>
  );
};

interface TextInputConditionProps {
  initialValue: string;
  placeholder: string;
  onSave: (value: string) => void;
}

const TextInputCondition: React.FC<TextInputConditionProps> = ({
  initialValue,
  placeholder,
  onSave,
}) => {
  const [inputValue, setInputValue] = useState(initialValue);

  useEffect(() => {
    setInputValue(initialValue);
  }, [initialValue]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleBlur = () => {
    onSave(inputValue);
  };

  return (
    <input
      type="text"
      value={inputValue}
      onChange={handleChange}
      onBlur={handleBlur}
      className="w-40 p-2 border rounded"
      placeholder={placeholder}
    />
  );
};
